import { useState, useEffect, type ChangeEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TablePagination,
} from "@mui/material";
import { getDisasterStatuses } from "../../../../api";
import type { DisasterStatusItem } from "../../../../types/disaster";

type ReportColumnId =
  | "display_code"
  | "customer_code"
  | "relation_code"
  | "code"
  | "branch_code"
  | "pref_name"
  | "customer_name"
  | "operator"
  | "name"
  | "sale_status"
  | "sale_detail_status"
  | "is_input"
  | "not_damage"
  | "personal_damage"
  | "property_damage"
  | "facility_damage"
  | "is_electric"
  | "recovery_status"
  | "reasons_closing_status"
  | "water_test_status"
  | "reasons_closing"
  | "damage_details"
  | "address"
  | "tel"
  | "owned_kind"
  | "resident_base_flag"
  | "is_power_gen"
  | "admin_report_code"
  | "edit_user_name";

type ReportColumn = {
  id: ReportColumnId;
  label: string;
  minWidth: number;
};

type SiteMaster = {
  display_code?: string;
  relation_code?: string;
  code?: string;
  name?: string;
  address?: string;
  tel?: string | null;
  operator?: string | null;
  owned_kind?: number | null;
  is_power_gen?: number | null;
  m_customer?: { code?: string; name?: string };
  m_branch?: { code?: string };
  m_division?: { pref_name?: string };
};

type EditUser = {
  name?: string;
  m_organizations?: Array<{
    m_role_id?: number | null;
    m_role?: { id?: number };
  }>;
};

const columns: ReportColumn[] = [
  { id: "display_code", label: "SSコード", minWidth: 100 },
  { id: "customer_code", label: "特約店コード", minWidth: 100 },
  { id: "relation_code", label: "納入先コード", minWidth: 100 },
  { id: "code", label: "カード系コード", minWidth: 100 },
  { id: "branch_code", label: "得意先管轄支店", minWidth: 120 },
  { id: "pref_name", label: "都道府県", minWidth: 100 },
  { id: "customer_name", label: "得意先", minWidth: 150 },
  { id: "operator", label: "販売店", minWidth: 150 },
  { id: "name", label: "SS名", minWidth: 150 },
  { id: "sale_status", label: "営業", minWidth: 100 },
  { id: "sale_detail_status", label: "営業状況", minWidth: 140 },
  { id: "is_input", label: "被害状況入力", minWidth: 100 },
  { id: "not_damage", label: "被害なし", minWidth: 80 },
  { id: "personal_damage", label: "人的被害あり", minWidth: 100 },
  { id: "property_damage", label: "第三者への物損あり", minWidth: 140 },
  { id: "facility_damage", label: "SS設備被害あり", minWidth: 120 },
  {
    id: "is_electric",
    label: "電話・インターネット接続不良有",
    minWidth: 180,
  },
  {
    id: "recovery_status",
    label: "点検記録簿（SS復旧チェック）実施で異常無し",
    minWidth: 220,
  },
  { id: "reasons_closing_status", label: "休止要因", minWidth: 120 },
  { id: "water_test_status", label: "浸水・冠水時 検水対応", minWidth: 150 },
  { id: "reasons_closing", label: "再開見込", minWidth: 150 },
  { id: "damage_details", label: "被害詳細・特記事項", minWidth: 200 },
  { id: "address", label: "住所", minWidth: 200 },
  { id: "tel", label: "電話番号", minWidth: 120 },
  { id: "owned_kind", label: "建設形態", minWidth: 100 },
  { id: "resident_base_flag", label: "住民拠点フラグ", minWidth: 100 },
  { id: "is_power_gen", label: "発電機フラグ", minWidth: 100 },
  { id: "admin_report_code", label: "行政報告用コード", minWidth: 120 },
  { id: "edit_user_name", label: "報告者", minWidth: 120 },
];

const saleStatusMap: Record<string, string> = {
  "0": "休業中",
  "1": "営業中",
  "2": "未入力",
  "9": "設定しない",
};

const saleDetailStatusMap: Record<string, string> = {
  "1": "平常営業",
  "2": "時間短縮営業",
  "3": "給油制限営業",
  "4": "緊急車両限定営業",
  "5": "営業停止(停電)",
  "6": "営業停止(施設損壊)",
  "7": "営業停止(その他)",
  "0": "不明",
};

const reasonsClosingMap: Record<string, string> = {
  "1": "停電",
  "2": "浸水・冠水",
  "3": "設備被害",
  "4": "人員被害",
  "5": "油切れ",
  "6": "計画休業",
};

const waterTestStatusMap: Record<string, string> = {
  "1": "実施済み",
  "2": "業者依頼中",
  "0": "未実施",
};

const ownedKindMap: Record<string, string> = {
  "1": "自己",
  "9": "社有",
};

const getSite = (row: DisasterStatusItem): SiteMaster | undefined =>
  row.site as SiteMaster | undefined;

const getEditUser = (row: DisasterStatusItem): EditUser | undefined => {
  const editUser = row.edit_user;
  if (editUser == null || typeof editUser !== "object") {
    return undefined;
  }
  return editUser as EditUser;
};

const getReporterName = (row: DisasterStatusItem): string => {
  const editUser = getEditUser(row);
  if (!editUser?.name) {
    return "";
  }
  const organization = editUser.m_organizations?.[0];
  const roleId = organization?.m_role_id ?? organization?.m_role?.id;
  if (roleId == null) {
    return "";
  }
  return editUser.name;
};

const getSaleStatusLabel = (
  status: string | number | null | undefined
): string => {
  if (status == null || status === "") {
    return "未入力";
  }
  return saleStatusMap[String(status)] ?? "";
};

const isTruthyFlag = (value: unknown): boolean =>
  value === true || value === 1 || value === "1" || value === "true";

const isDamageInput = (row: DisasterStatusItem): boolean =>
  row.damage_at != null && row.damage_at !== "";

const getCircleCross = (
  isInput: boolean,
  value: unknown,
  matchValue?: unknown
): string => {
  if (!isInput) {
    return "";
  }
  if (matchValue !== undefined) {
    return String(value) === String(matchValue) ? "〇" : "×";
  }
  return isTruthyFlag(value) ? "〇" : "×";
};

const getMappedValue = (
  value: string | number | null | undefined,
  map: Record<string, string>
): string => {
  if (value == null || value === "") {
    return "";
  }
  return map[String(value)] ?? "";
};

const InternalUse = () => {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(useLocation().search);
  const disasterId = searchParams.get("disaster-id");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ssData, setSsData] = useState<DisasterStatusItem[]>([]);
  const [totalData, setTotalData] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  const fetchSsData = async () => {
    if (!disasterId) {
      return;
    }
    setLoading(true);
    try {
      const response = await getDisasterStatuses(disasterId, {
        display_code: null,
        name: null,
        pref: null,
        m_branch_id: null,
        m_customer_id: null,
        latest_alive: null,
        latest_sales: null,
        latest_authori: null,
        is_input: null,
        is_damage: null,
        order: null,
        sort: null,
        page: page + 1,
        limit: rowsPerPage,
      });
      if (response.status !== "ok") {
        setError("データの取得に失敗しました");
        setLoading(false);
        return;
      }
      setSsData(response.disaster_statuses ?? []);
      setTotalData(response.total_count ?? 0);
      setLoading(false);
    } catch (fetchError) {
      console.error("APIエラー:", fetchError);
      setError("データの取得に失敗しました");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (disasterId) {
      fetchSsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disasterId, page, rowsPerPage]);

  const getCellValue = (row: DisasterStatusItem, columnId: ReportColumnId) => {
    const site = getSite(row);
    const isInput = isDamageInput(row);

    switch (columnId) {
      case "display_code":
        return site?.display_code ?? "";
      case "customer_code":
        return site?.m_customer?.code ?? "";
      case "relation_code":
        return site?.relation_code ?? "";
      case "code":
        return site?.code ?? "";
      case "branch_code":
        return site?.m_branch?.code ?? "";
      case "pref_name":
        return site?.m_division?.pref_name ?? "";
      case "customer_name":
        return site?.m_customer?.name ?? "";
      case "operator":
        return site?.operator ?? "";
      case "name":
        return site?.name ?? "";
      case "sale_status":
        return getSaleStatusLabel(row.sale_status);
      case "sale_detail_status":
        return getMappedValue(row.sale_detail_status, saleDetailStatusMap);
      case "is_input":
        return isInput ? "〇" : "×";
      case "not_damage":
        return getCircleCross(isInput, row.not_damage);
      case "personal_damage":
        return getCircleCross(isInput, row.personal_damage);
      case "property_damage":
        return getCircleCross(isInput, row.property_damage);
      case "facility_damage":
        return getCircleCross(isInput, row.facility_damage);
      case "is_electric":
        return getCircleCross(isInput, row.is_electric, 1);
      case "recovery_status":
        return getCircleCross(isInput, row.recovery_status, 1);
      case "reasons_closing_status":
        return getMappedValue(
          row.reasons_closing_status,
          reasonsClosingMap
        );
      case "water_test_status":
        return getMappedValue(row.water_test_status, waterTestStatusMap);
      case "reasons_closing":
        return row.reasons_closing ?? "";
      case "damage_details":
        return row.damage_details ?? "";
      case "address":
        return site?.address ?? "";
      case "tel":
        return site?.tel ?? "";
      case "owned_kind":
        return getMappedValue(site?.owned_kind, ownedKindMap);
      case "resident_base_flag":
        return "";
      case "is_power_gen":
        return site?.is_power_gen != null ? String(site.is_power_gen) : "";
      case "admin_report_code":
        return "";
      case "edit_user_name":
        return getReporterName(row);
      default:
        return "";
    }
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: "flex" }}>
        <Box
          onClick={() => navigate(-1)}
          sx={{
            display: "flex",
            height: "48px",
            alignItems: "center",
            paddingLeft: 5,
            fontSize: 20,
            fontWeight: "bold",
            whiteSpace: "nowrap",
            color: "primary.main",
            cursor: "pointer",
            textDecoration: "underline",
            "&:hover": { color: "primary.dark" },
            "&:active": { color: "primary.light" },
            "@media screen and (max-width:900px)": { paddingLeft: 2 },
          }}
        >
          ダッシュボード
        </Box>
        <Box
          sx={{
            display: "flex",
            height: "48px",
            alignItems: "center",
            fontSize: 20,
            fontWeight: "bold",
            whiteSpace: "nowrap",
          }}
        >
          / 被害状況レポート
        </Box>
      </Box>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="50vh"
        >
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <TableContainer>
            <Table
              stickyHeader
              aria-label="災害時店舗状況テーブル"
              size="small"
            >
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align="left"
                      style={{ minWidth: column.minWidth }}
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#f5f5f5",
                        borderBottom: "2px solid #333",
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {ssData.map((row, index) => (
                  <TableRow
                    hover
                    key={row.id}
                    {...({ name: `ssData_${index}` } as Record<string, string>)}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    {columns.map((column) => (
                      <TableCell key={`${row.id}-${column.id}`} align="left">
                        {getCellValue(row, column.id)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalData}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event: ChangeEvent<HTMLInputElement>) => {
              setRowsPerPage(+event.target.value);
              setPage(0);
            }}
            sx={{
              ".MuiToolbar-root,.MuiInputBase-root,.MuiTablePagination-actions":
                {
                  margin: "0 !important",
                  padding: "0 !important",
                },
            }}
            labelRowsPerPage="表示件数:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} / ${count !== -1 ? count : `more than ${to}`}`
            }
          />
        </Paper>
      )}
    </Box>
  );
};

export default InternalUse;
